import React, { useEffect, useState } from 'react';
import { hl7RouteAPI, systemAPI } from '../services/api';
import { AlertTriangle, CheckCircle, Cpu, HardDrive, Play, RefreshCw, RotateCcw, Server, Square, Terminal, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface HL7RouteStatus {
  service_running: boolean;
  tunnel_up: boolean;
  public_ip: string;
  forward_port: number;
  target_host: string;
  target_ip: string;
  target_port: number;
  mirth_listener_open: boolean;
  nat_configured: boolean;
  nat_rules: string[];
  ipsec_status: string;
  timestamp: string;
}

interface SystemStatus {
  service_running: boolean;
  active_connections: number;
  connections: Array<{ name: string; status: string; active: boolean }>;
  system: { cpu_percent: number; memory_percent: number; disk_percent: number; uptime: string };
  network: { bytes_sent: number; bytes_recv: number; packets_sent: number; packets_recv: number; interfaces: number };
  logs: string[];
  timestamp: string;
}

function CheckRow({ title, ok, detail }: { title: string; ok: boolean; detail: string }) {
  return (
    <div className="flex items-start justify-between rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-start gap-3">
        {ok ? <CheckCircle className="mt-0.5 h-5 w-5 text-green-600" /> : <XCircle className="mt-0.5 h-5 w-5 text-red-600" />}
        <div>
          <p className="font-medium text-gray-900">{title}</p>
          <p className="mt-1 text-sm text-gray-600">{detail}</p>
        </div>
      </div>
      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${ok ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {ok ? 'PASS' : 'FAIL'}
      </span>
    </div>
  );
}

function formatBytes(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export default function SystemMonitoring() {
  const [route, setRoute] = useState<HL7RouteStatus | null>(null);
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = async () => {
    try {
      const [routeResponse, systemResponse] = await Promise.all([
        hl7RouteAPI.getStatus(),
        systemAPI.getSystemStatus(),
      ]);
      setRoute(routeResponse.data);
      setStatus(systemResponse.data);
    } catch (error: any) {
      toast.error('Failed to load diagnostics: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, []);

  const serviceAction = async (action: 'start' | 'stop' | 'restart') => {
    const warning = action === 'stop'
      ? 'Stopping StrongSwan will drop active VPN tunnels. Continue?'
      : action === 'restart'
        ? 'Restarting StrongSwan may interrupt HL7 traffic briefly. Continue?'
        : 'Start StrongSwan service?';
    if (!window.confirm(warning)) return;

    try {
      setActionLoading(action);
      const response = action === 'start'
        ? await systemAPI.startService()
        : action === 'stop'
          ? await systemAPI.stopService()
          : await systemAPI.restartService();
      toast.success(response.data.message || `${action} requested`);
      await load();
    } catch (error: any) {
      toast.error(`Failed to ${action}: ` + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(null);
    }
  };

  if (loading || !route || !status) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="rounded-3xl bg-white p-8 shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
              <Terminal className="mr-2 h-4 w-4" /> Live operational checks
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Diagnostics</h1>
            <p className="mt-2 max-w-3xl text-gray-600">Confirm the full route before sending production HL7: StrongSwan, Docker NAT, Mirth TCP listener, and home tunnel state.</p>
          </div>
          <button onClick={load} className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh now
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200 lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900">Route checks</h2>
          <div className="mt-4 space-y-3">
            <CheckRow title="StrongSwan service" ok={route.service_running} detail="ipsec status responds from inside the VPN node container." />
            <CheckRow title="HL7 NAT rule" ok={route.nat_configured} detail={`tcp/${route.forward_port} is DNAT-forwarded to ${route.target_host}:${route.target_port}.`} />
            <CheckRow title="Mirth listener" ok={route.mirth_listener_open} detail={route.mirth_listener_open ? `${route.target_host}:${route.target_port} accepted a TCP connection.` : `${route.target_host}:${route.target_port} is refusing or not reachable from the VPN node.`} />
            <CheckRow title="Home IPsec tunnel" ok={route.tunnel_up} detail={route.tunnel_up ? 'StrongSwan reports an established security association.' : 'No established home peer yet; configure/start the home side.'} />
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Service controls</h2>
          <p className="mt-1 text-sm text-gray-600">Guarded actions. Stop/restart can interrupt HL7 traffic.</p>
          <div className="mt-5 space-y-3">
            <button onClick={() => serviceAction('start')} disabled={route.service_running || actionLoading === 'start'} className="flex w-full items-center justify-center rounded-lg bg-green-600 px-4 py-2 text-white disabled:opacity-50"><Play className="mr-2 h-4 w-4" />{actionLoading === 'start' ? 'Starting...' : 'Start'}</button>
            <button onClick={() => serviceAction('restart')} disabled={actionLoading === 'restart'} className="flex w-full items-center justify-center rounded-lg bg-amber-600 px-4 py-2 text-white disabled:opacity-50"><RotateCcw className="mr-2 h-4 w-4" />{actionLoading === 'restart' ? 'Restarting...' : 'Restart'}</button>
            <button onClick={() => serviceAction('stop')} disabled={!route.service_running || actionLoading === 'stop'} className="flex w-full items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-white disabled:opacity-50"><Square className="mr-2 h-4 w-4" />{actionLoading === 'stop' ? 'Stopping...' : 'Stop'}</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <h2 className="mb-4 flex items-center text-lg font-semibold text-gray-900"><Cpu className="mr-2 h-5 w-5" /> System</h2>
          <p className="text-sm text-gray-600">CPU: <strong>{status.system.cpu_percent.toFixed(1)}%</strong></p>
          <p className="mt-2 text-sm text-gray-600">Memory: <strong>{status.system.memory_percent.toFixed(1)}%</strong></p>
          <p className="mt-2 text-sm text-gray-600">Uptime: <strong>{status.system.uptime}</strong></p>
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <h2 className="mb-4 flex items-center text-lg font-semibold text-gray-900"><HardDrive className="mr-2 h-5 w-5" /> Disk / Network</h2>
          <p className="text-sm text-gray-600">Disk: <strong>{status.system.disk_percent.toFixed(1)}%</strong></p>
          <p className="mt-2 text-sm text-gray-600">Sent: <strong>{formatBytes(status.network.bytes_sent)}</strong></p>
          <p className="mt-2 text-sm text-gray-600">Received: <strong>{formatBytes(status.network.bytes_recv)}</strong></p>
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
          <h2 className="mb-4 flex items-center text-lg font-semibold text-gray-900"><Server className="mr-2 h-5 w-5" /> Endpoint</h2>
          <p className="text-sm text-gray-600">Public: <strong>{route.public_ip}:{route.forward_port}</strong></p>
          <p className="mt-2 text-sm text-gray-600">Internal: <strong>{route.target_host}:{route.target_port}</strong></p>
          <p className="mt-2 text-sm text-gray-600">Target IP: <strong>{route.target_ip || 'unresolved'}</strong></p>
        </div>
      </div>

      {!route.mirth_listener_open && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5" />
            <p className="text-sm">Mirth still appears closed from the VPN node. In OIE/Mirth, deploy the channel and ensure the TCP listener binds to <strong>0.0.0.0:{route.target_port}</strong> or the container interface, not only localhost.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl bg-slate-950 p-5 text-slate-100 shadow-sm">
          <h2 className="mb-3 font-semibold">NAT rules</h2>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-xs leading-5 text-slate-300">{route.nat_rules.length ? route.nat_rules.join('\n') : 'No HL7 NAT rules found.'}</pre>
        </div>
        <div className="rounded-2xl bg-slate-950 p-5 text-slate-100 shadow-sm">
          <h2 className="mb-3 font-semibold">Recent VPN logs</h2>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-xs leading-5 text-slate-300">{status.logs?.filter(Boolean).join('\n') || 'No recent logs.'}</pre>
        </div>
      </div>
    </div>
  );
}
